import { getServerErrorMessage } from '@src/shared/lib/error_handlers';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from './hooks';

type UseLoadErrorToastParams = {
  /** The data of the page could not be loaded. */
  isError: boolean;
  /** The failure; a new one after a retry shows the toast again. */
  error: unknown;
  title: string;
  /** Loads the data again; the toast's "Try again" calls it. */
  onRetry: () => void;
};

/**
 * Error toast for data a page could not load, with a "Try again" action.
 * It stays until it is closed or the data loads, and it closes when the
 * page goes away, so it does not linger over another page. The page keeps
 * its body empty meanwhile.
 */
export function useLoadErrorToast({
  isError,
  error,
  title,
  onRetry,
}: UseLoadErrorToastParams) {
  const { t } = useTranslation();
  const { dismiss, error: showError } = useToast();
  // The latest texts and retry, without showing the toast again for them.
  const latest = useRef({ title, onRetry, retryLabel: '' });
  latest.current = {
    title,
    onRetry,
    retryLabel: t('common.try_again', 'Try again'),
  };

  useEffect(() => {
    if (!isError) {
      return undefined;
    }
    const id = showError(latest.current.title, {
      description: getServerErrorMessage(error),
      action: {
        label: latest.current.retryLabel,
        onClick: () => {
          dismiss(id);
          latest.current.onRetry();
        },
      },
    });
    return () => dismiss(id);
  }, [dismiss, error, isError, showError]);
}
